import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { formatToUpper } from '@/utilities/formatter'

export const useMediaQueryStore = defineStore('mediaQuery', () => {
  interface MediaQueryContext {
    isUnderSp: boolean,
    isUnderPhablet: boolean,
    isUnderTablet: boolean,
  }

  const context: MediaQueryContext = reactive({
    isUnderSp: false,
    isUnderPhablet: false,
    isUnderTablet: false,
  })

  const mediaQueryIndicatorDOMRef = ref()
  const stateClassNameRef = ref()

  const spSize = computed<boolean>(() => {
    return context.isUnderSp
  })

  const phabletSize = computed<boolean>(() => {
    return !context.isUnderSp && context.isUnderPhablet
  })

  const tabletSize = computed<boolean>(() => {
    return !context.isUnderSp && !context.isUnderPhablet && context.isUnderTablet
  })

  const sizeName = computed<string>(() => {
    return spSize.value
      ? 'sp'
      : phabletSize.value
        ? 'phablet'
        : tabletSize.value
          ? 'tablet'
          : 'pc'
  })

  /**
   * MediaQueryObserver コンポーネント側から呼び出される初期化関数
   * @param mediaQueryIndicatorDOM コンポーネントが拾ったインジケーターの DOM
   * @param stateClassName 各メディアクエリごとに対応した CSS の state（状態）クラス名
   */
  const init = (
    mediaQueryIndicatorDOM: HTMLElement,
    stateClassName: {
      sp: string,
      phablet: string,
      tablet: string,
    },
  ): void => {
    mediaQueryIndicatorDOMRef.value = mediaQueryIndicatorDOM
    stateClassNameRef.value = stateClassName
  }

  const resetContext = (): void => {
    context.isUnderSp = false
    context.isUnderPhablet = false
    context.isUnderTablet = false
  }

  /**
   * MediaQueryObserver コンポーネント側で、CSS のメディアクエリごとに DOM の表示が切り替わる
   * その DOM の状態を監視し、store 側の各フラグを ON / OFF する
   * @description 各サイズ以下が担保されていればフラグが立つだけなので、例えば SP が true の場合、必然的に Table サイズ以下であることも成り立つので、Tablet も true になる。（あくまで、◯◯◯ サイズ以下かどうかのフラグ）
   *              よって、「◯◯◯ サイズかどうか」を拾いたい場合は、computed の方を使うこと
   */
  const setContext = (): void => {
    const $ = mediaQueryIndicatorDOMRef.value.firstElementChild

    if (!$ || formatToUpper($.tagName) !== 'UL') return

    const items: HTMLCollection = $.children

    if (items) {
      resetContext()

      Object.values(items).forEach(item => {
        const style = window.getComputedStyle(item)

        // sp 用のインジケーター DOM が表示状態の場合、SP サイズ以下のフラグが立つ
        if (item.classList.contains(stateClassNameRef.value.sp) && style.getPropertyValue('display') == 'block') {
          context.isUnderSp = true
        }

        // phablet 用のインジケーター DOM が表示状態の場合、Phablet サイズ以下のフラグが立つ
        if (item.classList.contains(stateClassNameRef.value.phablet) && style.getPropertyValue('display') == 'block') {
          context.isUnderPhablet = true
        }

        // tablet 用のインジケーター DOM が表示状態の場合、Tablet サイズ以下のフラグが立つ
        if (item.classList.contains(stateClassNameRef.value.tablet) && style.getPropertyValue('display') == 'block') {
          context.isUnderTablet = true
        }
      })
    }
  }

  return {
    // data
    context,

    // computed
    spSize,
    phabletSize,
    tabletSize,
    sizeName,

    // methods
    init,
    setContext,
  }
})
